import { useState } from "react";
import httpClient from "utils/httpClient";

interface Coordinates {
  x: number; // Longitude
  y: number; // Latitude
}

interface DistanceResponse {
  distance: number; // Distance in miles
  polyline: string; // Encoded polyline string
  fallback?: boolean; // Indicates if fallback was used
}

const useCalculateDistanceBetweenLocations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateDistance = async (
    from: Coordinates,
    to: Coordinates,
  ): Promise<DistanceResponse> => {
    if (!from || !to) return { distance: 0, polyline: "" };

    const origins = `${from.y},${from.x}`; // Latitude,Longitude for "From"
    const destinations = `${to.y},${to.x}`; // Latitude,Longitude for "To"
    const url = `/location/route/distance?origins=${origins}&destinations=${destinations}`;

    try {
      setLoading(true);
      setError(null);

      const response = await httpClient.get(url);
      const data = response.data;

      if (
        typeof data.distance === "number" &&
        typeof data.polyline === "string"
      ) {
        if (data.fallback) {
          setError("Accurate route unavailable, using estimated distance.");
        }
        return {
          distance: data.distance,
          polyline: data.polyline,
          fallback: data.fallback,
        };
      } else {
        setError("Failed to fetch distance");
        return { distance: 0, polyline: "" };
      }
    } catch (err) {
      setError("An error occurred while calculating distance.");
      return { distance: 0, polyline: "" };
    } finally {
      setLoading(false);
    }
  };

  return { calculateDistance, loading, error };
};

export default useCalculateDistanceBetweenLocations;
